import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useBooks } from '../context/BooksContext';
import { IoTrophy, IoHeart, IoEye, IoTime, IoRibbon, IoCalendar } from 'react-icons/io5';
import AdBanner from '../components/AdBanner';
import './Contest.css';

export default function Contest() {
  const navigate = useNavigate();
  const { books } = useBooks();
  const [contest, setContest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState('');

  // Charger les données du concours depuis Firestore
  useEffect(() => {
    const fetchContest = async () => {
      try {
        const d = await getDoc(doc(db, 'config', 'contest'));
        if (d.exists()) {
          setContest(d.data());
        }
      } catch (e) {}
      setLoading(false);
    };
    fetchContest();
  }, []);

  // Countdown jusqu'à la date de fin
  useEffect(() => {
    if (!contest?.endDate) return;

    const updateTimer = () => {
      const now = new Date();
      const end = new Date(contest.endDate + 'T23:59:59');
      const diff = end - now;

      if (diff <= 0) {
        setTimeLeft('Terminé');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setTimeLeft(`${days}j ${hours}h ${minutes}m`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000);
    return () => clearInterval(interval);
  }, [contest?.endDate]);

  // Filtrer les livres du concours (publiés entre startDate et endDate)
  const contestBooks = contest?.startDate && contest?.endDate
    ? books
        .filter(b => {
          if (b.status === 'draft') return false;
          const created = new Date(b.createdAt);
          return created >= new Date(contest.startDate) && created <= new Date(contest.endDate + 'T23:59:59');
        })
        .sort((a, b) => (b.likes || 0) - (a.likes || 0))
    : [];

  const MEDALS = ['🥇', '🥈', '🥉'];

  if (loading) {
    return <div className="page" style={{ textAlign: 'center', paddingTop: 60, opacity: 0.3 }}>Chargement...</div>;
  }

  if (!contest || !contest.active) {
    return (
      <div className="page contest-page">
        <div className="contest-inactive">
          <span className="contest-inactive-icon">🏆</span>
          <h2>Pas de concours en cours</h2>
          <p>Reviens bientôt pour participer au prochain concours Plumly !</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page contest-page">
      <div className="contest-header">
        <motion.div
          className="contest-trophy"
          animate={{ rotate: [0, -5, 5, -5, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 4 }}
        >
          🏆
        </motion.div>
        <h1 className="contest-title">{contest.title || 'Concours'}</h1>
        {contest.theme && <p className="contest-theme">Thème : <strong>{contest.theme}</strong></p>}
        {contest.description && <p className="contest-subtitle">{contest.description}</p>}

        <div className="contest-meta">
          {contest.startDate && contest.endDate && (
            <div className="contest-dates">
              <IoCalendar />
              <span>
                Du {new Date(contest.startDate).toLocaleDateString('fr-FR')} au {new Date(contest.endDate).toLocaleDateString('fr-FR')}
              </span>
            </div>
          )}
          {timeLeft && timeLeft !== 'Terminé' && (
            <div className="contest-timer">
              <IoTime /> Fin dans : <strong>{timeLeft}</strong>
            </div>
          )}
          {timeLeft === 'Terminé' && (
            <div className="contest-timer contest-ended">
              <IoTime /> Concours terminé
            </div>
          )}
        </div>
      </div>

      {/* Prix à gagner */}
      {contest.prizes && contest.prizes.length > 0 && (
        <div className="contest-prizes">
          <h3 className="contest-prizes-title"><IoRibbon /> Prix à gagner</h3>
          <div className="contest-prizes-list">
            {contest.prizes.map((prize, i) => (
              <motion.div
                key={i}
                className="contest-prize-card"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <span className="contest-prize-medal">{['🥇', '🥈', '🥉'][i] || '🎁'}</span>
                <span className="contest-prize-label">{i === 0 ? '1er prix' : i === 1 ? '2ème prix' : i === 2 ? '3ème prix' : `${i + 1}ème prix`}</span>
                <span className="contest-prize-text">{prize}</span>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Règles */}
      <div className="contest-rules">
        <h3>📜 Comment participer ?</h3>
        <div className="contest-rules-steps">
          <div className="contest-rule-step">
            <span className="contest-step-num">1</span>
            <p>Publie un livre pendant la durée du concours{contest.theme ? ` sur le thème "${contest.theme}"` : ''}</p>
          </div>
          <div className="contest-rule-step">
            <span className="contest-step-num">2</span>
            <p>Les lecteurs votent en likant ❤️ ton livre</p>
          </div>
          <div className="contest-rule-step">
            <span className="contest-step-num">3</span>
            <p>Le livre avec le plus de likes à la fin du concours gagne !</p>
          </div>
        </div>
        <motion.button
          className="contest-participate-btn"
          onClick={() => navigate('/author/write')}
          whileTap={{ scale: 0.97 }}
        >
          ✍️ Participer — Écrire un livre
        </motion.button>
      </div>

      {/* Pub */}
      <AdBanner slot="contest-mid" format="horizontal" />

      {/* Classement */}
      <h2 className="contest-ranking-title">
        <IoTrophy /> Classement {timeLeft === 'Terminé' ? 'final' : 'en direct'} ({contestBooks.length} participant{contestBooks.length > 1 ? 's' : ''})
      </h2>

      <div className="contest-list">
        {contestBooks.length > 0 ? (
          contestBooks.map((book, i) => (
            <motion.div
              key={book.id}
              className={'contest-entry ' + (i < 3 ? 'contest-entry-top' : '')}
              onClick={() => navigate('/book/' + book.id)}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="contest-rank">{i < 3 ? MEDALS[i] : '#' + (i + 1)}</span>
              <div className="contest-cover" style={{
                background: book.coverImage
                  ? `url(${book.coverImage}) center/cover no-repeat`
                  : book.coverGradient || 'linear-gradient(135deg, #2a2520, #1a1714)'
              }} />
              <div className="contest-info">
                <h3 className="contest-book-title">{book.title}</h3>
                <p className="contest-book-author">{book.author}</p>
                <div className="contest-book-stats">
                  <span><IoHeart style={{ color: 'var(--rose)' }} /> {book.likes || 0}</span>
                  <span><IoEye /> {book.views || 0}</span>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">📝</div>
            <p className="empty-state-text">Aucun participant pour le moment. Sois le premier !</p>
            <button className="btn btn-primary" onClick={() => navigate('/author/write')}>
              Écrire un livre
            </button>
          </div>
        )}
      </div>
            <AdBanner slot="contest-bottom" format="horizontal" />

    </div>
  );
}